"use client";

import React, { useEffect, useRef, useState } from "react";
import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const textareaClasses = "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
function GatewayForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center space-y-4 py-8 text-center">
        <div className="rounded-full bg-primary/10 p-3">
          <svg className="h-6 w-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <p className="text-lg font-medium text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Submit Another Request
        </Button>
      </div>;
  }
  const investmentOptions = ["Data Centers", "GPU Farms", "AI Cloud Services", "Edge Computing", "AI Software Platforms"];
  return <Form onSubmit={handleSubmit}>
      <div className="grid gap-4 py-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="full_name" render={({
          field
        }) => <FormItem>
                <FormLabel>Full Name</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="Jane Smith" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel>Company / Organization</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="XYZ Capital" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="title" render={({
          field
        }) => <FormItem>
                <FormLabel>Title / Position</FormLabel>
                <FormControl>
                  <input type="text" className={inputClasses} placeholder="Managing Director" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel>Email Address</FormLabel>
                <FormControl>
                  <input type="email" className={inputClasses} placeholder="jane@xyz.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField control={control} name="phone" render={({
          field
        }) => <FormItem>
                <FormLabel>Phone Number</FormLabel>
                <FormControl>
                  <input type="tel" className={inputClasses} placeholder="+1 (555) 123-4567" {...field} />
                </FormControl>
                <FormDescription>Optional, for follow-up calls</FormDescription>
                <FormMessage />
              </FormItem>} />
          <FormField control={control} name="aum" render={({
          field
        }) => <FormItem>
                <FormLabel>Assets Under Management (USD millions)</FormLabel>
                <FormControl>
                  <input type="number" className={inputClasses} placeholder="500" {...field} />
                </FormControl>
                <FormDescription>Provide approximate AUM in millions</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="investment_interests" render={() => <FormItem>
              <div className="mb-4">
                <FormLabel className="text-base">Areas of Interest</FormLabel>
                <FormDescription>Select all that apply</FormDescription>
              </div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {investmentOptions.map((item, i) => <FormField key={item} control={control} name="investment_interests" render={({
            field
          }) => {
            return <FormItem key={item} data-index={i} className="flex flex-row items-start space-x-3 space-y-0 rounded-md border border-input p-3 shadow-sm">
                          <FormControl>
                            <input type="checkbox" className="h-4 w-4 shrink-0 rounded-sm border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground" checked={field.value?.includes(item)} onChange={e => {
                  const checked = e.target.checked;
                  return checked ? field.onChange([...(field.value || []), item]) : field.onChange(field.value?.filter((value: string) => value !== item));
                }} />
                          </FormControl>
                          <FormLabel className="font-normal cursor-pointer w-full">
                            {item}
                          </FormLabel>
                        </FormItem>;
          }} />)}
              </div>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem>
              <FormLabel>Additional Details</FormLabel>
              <FormControl>
                <textarea className={textareaClasses} placeholder="Tell us about your investment focus..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
            {submitError}
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full mt-4">
          {isSubmitting ? <>
              <Loader className="mr-2 h-4 w-4 animate-spin" />
              Submitting Request...
            </> : "Submit Request"}
        </Button>
      </div>
    </Form>;
}
export default function InstitutionalGateway() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="institutional-gateway" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32 lg:py-40">
      <style dangerouslySetInnerHTML={{
      __html: `
        #institutional-gateway {
          
          --background: 42 71% 97%;    
          --foreground: 24 10% 10%;    
          --card: 43 59% 90%;          
          --accent: 26 91% 37%;        

          
          --primary: 42 71% 97%;
          --primary-foreground: 24 10% 10%;
          --secondary: 43 59% 90%;
          --secondary-foreground: 24 10% 10%;

          
          --muted: 43 59% 90%;
          --muted-foreground: 24 10% 10%;
          --border: 24 10% 10% / 0.2;
          --input: 24 10% 10% / 0.2;

          
          --ring: 26 91% 37%;
        }
      `
    }} />
      {}
      <div className="absolute inset-0 z-0 opacity-5 mix-blend-luminosity">
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1587878/img/data_center_wireframe_bg.png)] bg-cover bg-center bg-no-repeat" />
      </div>
      
      {}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-background/40 via-background/80 to-background" />

      <div ref={ref} className={`container relative z-10 mx-auto px-4 text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-8 text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl lg:text-7xl">
            Secure Your Position in the AI Build-out.
          </h2>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button size="lg" className="h-14 px-8 text-lg font-medium bg-accent text-accent-foreground shadow-lg shadow-accent/20 transition-all hover:bg-accent/90 hover:shadow-accent/40 hover:-translate-y-1">
                Request Private Access
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto border-border/50 bg-background/95 backdrop-blur-xl">
              <DialogHeader className="mb-4">
                <DialogTitle className="text-2xl">Institutional Gateway</DialogTitle>
                <DialogDescription className="text-base">
                  Request private access to N2N Capital's AI infrastructure investment platform. Please provide your details below.
                </DialogDescription>
              </DialogHeader>
              
              <FormProvider formId="institutional_gateway_form_intelligence_engine" sectionName="Institutional Gateway" defaultValues={{
              full_name: "",
              company: "",
              title: "",
              email: "",
              phone: "",
              aum: "",
              investment_interests: [],
              message: ""
            }} validationRules={{
              full_name: {
                required: "Full Name is required"
              },
              company: {
                required: "Company is required"
              },
              title: {
                required: "Title is required"
              },
              email: {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Please enter a valid email address"
                }
              },
              investment_interests: {
                required: "Please select at least one area of interest"
              }
            }} submitText="Submit Request" successMessage="Thank you. Your request for private access has been received. Our team will contact you shortly.">
                <GatewayForm />
              </FormProvider>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>;
}